import React from "react";
import { useTranslation } from "react-i18next";
import { AuthImg } from "@/lib/authMedia";
import { pictureStateOf, type FaceLockAvatar } from "@/lib/facelock";
import { isStillsOnly, videoEtaMinutes } from "@/lib/facelock-stills";
import { AvatarSilhouette } from "./FaceLockParts";

// Pick a FaceLock instead of uploading selfies — shared by the corrido flow
// (above the artist photo uploader) and the ads flow (in place of the old
// trained-face <select>). The host renders it only when there is at least one
// castable FaceLock, and hides its own uploader while one is chosen. `null`
// means "new photos": the host's uploader, exactly as before FaceLock.
//
// Inline styles on purpose: both hosts are inline-styled chat cards with their
// own palettes, passed in here.
//
// A tile shows the avatar's portrait (drawn with the avatar itself), never an
// uploaded photo and never a letter: while it has none, a silhouette, and
// "Drawing…" while it is being drawn.

export type FaceLockPickerPalette = {
  accent: string;
  /** The chosen option's background. */
  accentSoft: string;
  border: string;
  surface: string;
  white: string;
  text: string;
  muted: string;
};

const DEFAULT_PALETTE: FaceLockPickerPalette = {
  accent: "#38bdf8",
  accentSoft: "rgba(56,189,248,.08)",
  border: "#2a3648",
  surface: "#0a0e15",
  white: "#ffffff",
  text: "#e2e8f0",
  muted: "#94a3b8",
};

type Props = {
  avatars: FaceLockAvatar[];
  value: string | null;
  onChange: (avatarId: string | null) => void;
  palette?: FaceLockPickerPalette;
  testIdPrefix?: string;
  disabled?: boolean;
  /** false: no "New photos" option (FACELOCK_CREW_REQUIRED: a FaceLock is the only way in). */
  allowNewPhotos?: boolean;
  /** Overrides the hint line under the title. */
  hint?: string;
};

/** No portrait yet: a silhouette, saying "Drawing…" while it is being drawn. */
function PictureTile({ avatar, tile, palette, testId }: {
  avatar: FaceLockAvatar; tile: React.CSSProperties; palette: FaceLockPickerPalette; testId: string;
}) {
  const { t } = useTranslation();
  const state = pictureStateOf(avatar);
  const drawing = state === "drawing" || state === "learning" || state === "waiting";
  return (
    <span
      data-testid={testId}
      data-picture-state={state}
      title={drawing ? t("faceLock.picture.drawing") : undefined}
      style={{ ...tile, position: "relative", display: "flex", alignItems: "flex-end", justifyContent: "center", overflow: "hidden", background: palette.border, color: palette.muted }}
    >
      <AvatarSilhouette style={{ width: "80%", height: "auto", opacity: 0.5 }} />
      {drawing && (
        <span style={{ position: "absolute", left: 0, right: 0, bottom: 0, padding: "2px 0", fontSize: 9, fontWeight: 700, lineHeight: 1.2, color: palette.white, background: "rgba(0,0,0,.55)" }}>
          {t("faceLock.picture.drawingShort")}
        </span>
      )}
    </span>
  );
}

export default function FaceLockPicker({ avatars, value, onChange, palette = DEFAULT_PALETTE, testIdPrefix = "facelock-picker", disabled, allowNewPhotos = true, hint }: Props) {
  const { t } = useTranslation();
  const chosen = avatars.find((a) => a.id === value) || null;
  const option = (on: boolean): React.CSSProperties => ({
    display: "flex", flexDirection: "column", alignItems: "center", gap: 4, width: 72, padding: 4,
    borderRadius: 9, cursor: disabled ? "default" : "pointer", textAlign: "center",
    background: on ? palette.accentSoft : palette.surface,
    border: `${on ? 2 : 1}px solid ${on ? palette.accent : palette.border}`,
    opacity: disabled ? 0.6 : 1,
  });
  const tile: React.CSSProperties = { width: 60, height: 80, objectFit: "cover", borderRadius: 6, display: "block" };
  return (
    <div data-testid={testIdPrefix} style={{ display: "grid", gap: 6, marginBottom: 8 }}>
      <div style={{ fontWeight: 700, color: palette.white, fontSize: 12 }}>{t("faceLock.picker.title")}</div>
      <div style={{ fontSize: 12, color: palette.muted, lineHeight: 1.4 }}>{hint ?? t("faceLock.picker.hint")}</div>
      <div role="radiogroup" aria-label={t("faceLock.picker.title")} style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {avatars.map((a) => {
          const on = a.id === value;
          return (
            <button
              key={a.id}
              type="button"
              role="radio"
              aria-checked={on}
              disabled={disabled}
              data-testid={`${testIdPrefix}-option-${a.id}`}
              onClick={() => onChange(a.id)}
              style={option(on)}
            >
              {a.picture_url
                ? <AuthImg src={a.picture_url} alt="" style={tile} />
                : <PictureTile avatar={a} tile={tile} palette={palette} testId={`${testIdPrefix}-picture-${a.id}`} />}
              <span style={{ fontSize: 11, fontWeight: 700, color: on ? palette.accent : palette.text, maxWidth: 64, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</span>
              {isStillsOnly(a) && (
                <span data-testid={`${testIdPrefix}-video-soon-${a.id}`} style={{ fontSize: 9, fontWeight: 700, color: palette.muted, lineHeight: 1.2 }}>
                  {t("faceLock.picker.videoSoonBadge")}
                </span>
              )}
            </button>
          );
        })}
        {allowNewPhotos && (
          <button
            type="button"
            role="radio"
            aria-checked={!chosen}
            disabled={disabled}
            data-testid={`${testIdPrefix}-none`}
            onClick={() => onChange(null)}
            style={option(!chosen)}
          >
            <span aria-hidden style={{ ...tile, display: "flex", alignItems: "center", justifyContent: "center", border: `1px dashed ${palette.border}`, color: palette.muted, fontSize: 24 }}>+</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: !chosen ? palette.accent : palette.text }}>{t("faceLock.picker.newPhotos")}</span>
          </button>
        )}
      </div>
      {chosen && (
        <div data-testid={`${testIdPrefix}-using`} style={{ fontSize: 12, color: palette.accent, fontWeight: 700 }}>
          {t("faceLock.picker.using", { name: chosen.name })}
        </div>
      )}
      {chosen && isStillsOnly(chosen) && (
        // STILLS FIRST: the storyboard can be made now; the video waits for its video files.
        <div data-testid={`${testIdPrefix}-video-soon`} style={{ fontSize: 12, color: palette.muted, lineHeight: 1.4 }}>
          {videoEtaMinutes(chosen) !== null
            ? t("faceLock.picker.videoSoon", { name: chosen.name, minutes: videoEtaMinutes(chosen) })
            : t("faceLock.picker.videoSoonNoEta", { name: chosen.name })}
        </div>
      )}
    </div>
  );
}
